import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { ShieldCheckIcon } from '@heroicons/react/24/outline'
import { authAPI } from '../../services/api'
import { SESSIONS_QUERY_KEY } from '../profile/sessionsQueries'
import { wasCancelled, withConfirmation } from './confirmCode'
import RecoveryCodes from './RecoveryCodes'
import SecondFactorSetup from './SecondFactorSetup'
import { SECURITY_QUERY_KEY } from './securityQueries'

// The server sends naive UTC (no zone); read it as UTC.
const asUtc = (value) => new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`)

/**
 * VH-285 — the authenticator on the Security page: active since when, how
 * many recovery codes are left with "Make new codes", "Set up a new phone",
 * and this and the previous login. Both actions ask for a current code first
 * when the last one is more than five minutes old.
 */
export default function SecondFactorCard() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  // What the card shows instead of the overview: new codes, or a set-up.
  const [codes, setCodes] = useState(null)
  const [replacing, setReplacing] = useState(null)

  const overview = useQuery({
    queryKey: SECURITY_QUERY_KEY,
    queryFn: () => authAPI.getSecurity().then((response) => response.data),
  })

  const when = (value) =>
    value
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(asUtc(value))
      : t('secondFactor.card.never')

  const run = async (action, onDone) => {
    setBusy(true)
    try {
      onDone((await withConfirmation(action)).data)
    } catch (error) {
      if (!wasCancelled(error)) toast.error(t('secondFactor.card.failed'))
    } finally {
      setBusy(false)
    }
  }

  const done = () => {
    setCodes(null)
    setReplacing(null)
    queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEY })
    queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY })
  }

  const data = overview.data

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center">
          <ShieldCheckIcon className="mr-2 h-6 w-6 text-indigo-600" />
          <h3 className="text-lg font-medium text-gray-900">{t('secondFactor.card.title')}</h3>
        </div>
      </div>
      <div className="card-body space-y-4">
        {overview.isLoading && <div className="h-16 animate-pulse rounded-lg bg-gray-100" />}
        {overview.isError && (
          <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <span>{t('secondFactor.card.loadError')}</span>
            <button type="button" onClick={() => overview.refetch()} className="font-medium underline">
              {t('openSessions.retry')}
            </button>
          </div>
        )}

        {codes && <RecoveryCodes codes={codes} onDone={done} doneLabel={t('secondFactor.card.done')} />}

        {replacing && (
          <SecondFactorSetup
            setup={replacing}
            send={(code) => authAPI.activateReplace(code)}
            onDone={(answer) => {
              setReplacing(null)
              setCodes(answer.recovery_codes)
            }}
          >
            <button type="button" onClick={() => setReplacing(null)} className="btn-secondary w-full sm:flex-1">
              {t('common.cancel')}
            </button>
          </SecondFactorSetup>
        )}

        {data && !codes && !replacing && (
          <>
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-gray-500">{t('secondFactor.card.activeSince')}</dt>
                <dd className="text-gray-900">{when(data.second_factor_since)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">{t('secondFactor.card.entry')}</dt>
                <dd className="text-gray-900">{data.issuer}</dd>
              </div>
              <div>
                <dt className="text-gray-500">{t('secondFactor.card.thisLogin')}</dt>
                <dd className="text-gray-900">{when(data.this_login)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">{t('secondFactor.card.previousLogin')}</dt>
                <dd className="text-gray-900">{when(data.previous_login)}</dd>
              </div>
            </dl>
            <p className={`text-sm ${data.recovery_codes_left < 3 ? 'text-red-700' : 'text-gray-700'}`}>
              {t('secondFactor.card.codesLeft', { count: data.recovery_codes_left })}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => authAPI.newRecoveryCodes(), (answer) => setCodes(answer.recovery_codes))}
                className="btn-secondary"
              >
                {t('secondFactor.card.newCodes')}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => authAPI.beginReplace(), setReplacing)}
                className="btn-secondary"
              >
                {t('secondFactor.card.replace')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
